/**
 * DonationStep.tsx - Fundraiser Builder Step 2: Donation Settings
 *
 * This component handles the second step of the fundraiser builder where users
 * configure donation settings, payment options, and donation form behavior.
 *
 * Key Features:
 * - Donation item configuration and setup
 * - Merchant account selection and management
 * - Donation form type selection (embedded vs popup)
 * - Donation restrictions and limits
 * - After-donation behavior settings
 * - Payment method configuration
 * - Form validation and error handling
 *
 * Configuration Sections:
 * - Donation Item: Product-based donations with quantities
 * - Merchant Account: Payment processing account selection
 * - Donation Form: Form type and modal configuration
 * - After Donation: Post-donation redirect and behavior
 * - Restrictions: Minimum/maximum donation limits
 *
 * Props:
 * - errors: Record<string, string> - Validation errors
 * - shouldShowError: () => boolean - Error display logic
 *
 * Dependencies: MobX store, donation section components, validation
 */

import React from "react";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import { fundraiserStore } from "../../stores/FundraiserStore";
import { DonationItemSection } from "./donation/DonationItemSection";
import { MerchantAccountSection } from "./donation/MerchantAccountSection";
import { DonationFormSection } from "./donation/DonationFormSection";
import { AfterDonationSection } from "./donation/AfterDonationSection";
import { DonationAmountSelection } from "../../enums";
import { CreateFundraiserModel } from "../../models";

interface DonationStepProps {
  shouldShowError: (field: string) => boolean;
}

const StepContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
`;

export const DonationStep: React.FC<DonationStepProps> = observer(
  ({ shouldShowError }) => {
    const { builderData } = fundraiserStore;

    // Sort custom donation amounts before saving
    const sortCustomAmounts = () => {
      if (
        builderData.donationAmountsType === DonationAmountSelection.Custom &&
        builderData.customDonationAmounts
      ) {
        const sortedAmounts = [...builderData.customDonationAmounts].sort(
          (a, b) => a - b
        );
        fundraiserStore.updateBuilderField(
          "customDonationAmounts",
          sortedAmounts
        );
      }
    };

    // Save donation settings to localStorage
    const saveDonationSettings = () => {
      // Sort amounts before saving
      sortCustomAmounts();

      const donationSettings = {
        donationItem: builderData.donationItem,
        isCustomDonationItem: builderData.isCustomDonationItem,
        donationAmountsType: builderData.donationAmountsType,
        customDonationAmounts: builderData.customDonationAmounts,
        allowCustomDonations: builderData.allowCustomDonations,
        allowMonthlyDonations: builderData.allowMonthlyDonations,
        merchantAccount: builderData.merchantAccount,
        donationFormType: builderData.donationFormType,
        popupModalType: builderData.popupModalType,
        donationRestrictionsEnabled: builderData.donationRestrictionsEnabled,
        minDonationAmount: builderData.minDonationAmount,
        maxDonationAmount: builderData.maxDonationAmount,
      };

      try {
        localStorage.setItem(
          "fundraiser_donation_settings",
          JSON.stringify(donationSettings)
        );
        console.log("Donation settings saved to localStorage");
      } catch (error) {
        console.error("Error saving donation settings to localStorage:", error);
      }
    };

    // Load donation settings from localStorage on component mount
    // React.useEffect(() => {
    //   try {
    //     const savedSettings = localStorage.getItem(
    //       "fundraiser_donation_settings"
    //     );
    //     console.log(savedSettings);
    //     if (savedSettings) {
    //       const parsedSettings = JSON.parse(savedSettings);

    //       // Update component state with saved values
    //       if (parsedSettings.donationItem) {
    //         fundraiserStore.updateBuilderField(
    //           "donationItem",
    //           parsedSettings.donationItem
    //         );
    //       }
    //       if (parsedSettings.isCustomDonationItem !== undefined) {
    //         fundraiserStore.updateBuilderField(
    //           "isCustomDonationItem",
    //           parsedSettings.isCustomDonationItem
    //         );
    //       }
    //       if (parsedSettings.donationAmountsType) {
    //         fundraiserStore.updateBuilderField(
    //           "donationAmountsType",
    //           parsedSettings.donationAmountsType
    //         );
    //       }
    //       if (parsedSettings.customDonationAmounts) {
    //         fundraiserStore.updateBuilderField(
    //           "customDonationAmounts",
    //           parsedSettings.customDonationAmounts
    //         );
    //       }
    //       if (parsedSettings.allowCustomDonations !== undefined) {
    //         fundraiserStore.updateBuilderField(
    //           "allowCustomDonations",
    //           parsedSettings.allowCustomDonations
    //         );
    //       }
    //       if (parsedSettings.allowMonthlyDonations !== undefined) {
    //         fundraiserStore.updateBuilderField(
    //           "allowMonthlyDonations",
    //           parsedSettings.allowMonthlyDonations
    //         );
    //       }
    //       if (parsedSettings.merchantAccount) {
    //         fundraiserStore.updateBuilderField(
    //           "merchantAccount",
    //           parsedSettings.merchantAccount
    //         );
    //       }
    //       if (parsedSettings.donationFormType) {
    //         fundraiserStore.updateBuilderField(
    //           "donationFormType",
    //           parsedSettings.donationFormType
    //         );
    //       }
    //       if (parsedSettings.popupModalType) {
    //         fundraiserStore.updateBuilderField(
    //           "popupModalType",
    //           parsedSettings.popupModalType
    //         );
    //       }
    //       if (parsedSettings.donationRestrictionsEnabled !== undefined) {
    //         fundraiserStore.updateBuilderField(
    //           "donationRestrictionsEnabled",
    //           parsedSettings.donationRestrictionsEnabled
    //         );
    //       }
    //       if (parsedSettings.minDonationAmount) {
    //         fundraiserStore.updateBuilderField(
    //           "minDonationAmount",
    //           parsedSettings.minDonationAmount
    //         );
    //       }
    //       if (parsedSettings.maxDonationAmount) {
    //         fundraiserStore.updateBuilderField(
    //           "maxDonationAmount",
    //           parsedSettings.maxDonationAmount
    //         );
    //       }

    //       console.log("Donation settings loaded from localStorage");
    //     }
    //   } catch (error) {
    //     console.error(
    //       "Error loading donation settings from localStorage:",
    //       error
    //     );
    //   }
    // }, []);

    const updateBuilderField = (
      field: keyof CreateFundraiserModel,
      value: any
    ) => {
      fundraiserStore.updateBuilderField(field, value);
    };

    return (
      <StepContainer>
        <DonationItemSection
          builderData={builderData}
          updateBuilderField={updateBuilderField}
        />

        <MerchantAccountSection
          builderData={builderData}
          updateBuilderField={updateBuilderField}
        />

        <DonationFormSection
          builderData={builderData}
          updateBuilderField={updateBuilderField}
        />

        <AfterDonationSection
          builderData={builderData}
          updateBuilderField={updateBuilderField}
        />
      </StepContainer>
    );
  }
);
